import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Alert, Button, Spinner, Form } from 'react-bootstrap';
import Select from 'react-select';
import { toast } from 'react-toastify';
import { Scale, Undo2, CheckCircle2, AlertTriangle, ShieldCheck } from 'lucide-react';
import axios from '../../../../services/AxiosInstance';
import { getApiUrl } from '../../../../utils/ApiUtils';
import { brandSelectTheme } from '../../Common/reactSelectTheme';
import { OwnAccountChip } from '../../../../utils/accountPickerOptions';
import {
  formatCurrency,
  formatNumber,
  formatDate,
  unwrapData,
  k1ReasonMessage,
  K1_ALTERNATIVE_LABELS,
  K1_STATUTORY_KIND_LABELS,
  ASSET_FORM_STYLES,
} from './assetUtils';

/**
 * K1 årsvärdering — the collective fiscal-year valuation of maskiner/
 * inventarier + förvärvade immateriella (BFNAR 2006:1 p. 6.37-6.38, IL
 * 18 kap.) and the tax-linked property depreciation (p. 6.14-6.15).
 *
 * The panel shows exactly what the backend computed (opening value, årets
 * anskaffningar, ersättningar, underlag, the permitted alternatives with
 * their sources) and posts ONE year-end verifikat after explicit selection.
 * All calculation happens server-side; the panel echoes the previewed
 * underlag/closing value back so a stale preview can never post
 * (K1_PREVIEW_STALE). Structured review requirements block posting and are
 * rendered as actionable Swedish text — never raw reason codes.
 */
const K1YearEndPanel = ({ companyId }) => {
  const [fiscalYears, setFiscalYears] = useState([]);
  const [selectedYear, setSelectedYear] = useState(null);
  const [preview, setPreview] = useState(null);
  const [poolYears, setPoolYears] = useState([]);
  const [loading, setLoading] = useState(false);
  const [posting, setPosting] = useState(false);
  const [error, setError] = useState(null);
  const [alternative, setAlternative] = useState(null);
  const [kindRules, setKindRules] = useState({});
  const [customValue, setCustomValue] = useState('');
  const [postingPreview, setPostingPreview] = useState(null);

  const loadPoolYears = useCallback(async () => {
    try {
      const res = await axios.get(getApiUrl(`${companyId}/assets/k1/pool-years`), { withCredentials: true });
      setPoolYears(unwrapData(res)?.poolYears || []);
    } catch (e) {
      // History is informational — the preview flow surfaces real errors.
    }
  }, [companyId]);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(getApiUrl(`${companyId}/accounting/fiscalyears`), { withCredentials: true });
        const years = (res.data?.fiscalYears || [])
          .sort((a, b) => String(b.end_date).localeCompare(String(a.end_date)));
        setFiscalYears(years);
        const open = years.find((fy) => fy.state === 'open');
        if (open) setSelectedYear({ value: open.fiscal_year_id, label: `${open.year} (${String(open.start_date).slice(0, 10)} – ${String(open.end_date).slice(0, 10)})` });
      } catch (e) {
        setError('Räkenskapsåren kunde inte hämtas.');
      }
    })();
    loadPoolYears();
  }, [companyId, loadPoolYears]);

  const fetchPreview = useCallback(async (fiscalYearId) => {
    setLoading(true);
    setError(null);
    setPreview(null);
    setPostingPreview(null);
    setAlternative(null);
    try {
      const res = await axios.get(
        getApiUrl(`${companyId}/assets/k1/year-end/preview`),
        { params: { fiscalYearId }, withCredentials: true }
      );
      const data = unwrapData(res);
      setPreview(data);
      // Default to the largest permitted deduction, but never auto-post.
      // With several statutory kinds, the lawful per-kind mix (IL 18:17
      // 2 men.) is often the lowest — default each kind to its cheaper rule.
      const alt = data?.alternatives;
      if (alt) {
        const kinds = Object.entries(alt.perKind || {})
          .filter(([, k]) => (k.underlag > 0 || k.gross > 0));
        const defaultRules = Object.fromEntries(kinds.map(([kind, k]) => [
          kind,
          k.supplRuleValue !== null && k.supplRuleValue < k.mainRuleValue ? 'suppl' : 'main',
        ]));
        setKindRules(defaultRules);
        if (alt.lowValueAvailable) setAlternative('low_value_full');
        else if (kinds.length > 1
          && new Set(Object.values(defaultRules)).size > 1) setAlternative('per_kind');
        else if (alt.supplRuleValue !== null && alt.supplRuleValue < alt.mainRuleValue) setAlternative('suppl_rule');
        else setAlternative('main_rule');
      }
    } catch (e) {
      setError(k1ReasonMessage(e));
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    if (selectedYear?.value) fetchPreview(selectedYear.value);
  }, [selectedYear, fetchPreview]);

  // Server-side posting preview for the chosen alternative (exact rows).
  const fetchPostingPreview = useCallback(async () => {
    if (!selectedYear?.value || !alternative) return;
    try {
      const res = await axios.post(
        getApiUrl(`${companyId}/assets/k1/year-end/preview`),
        {
          fiscalYearId: selectedYear.value,
          selection: {
            alternative,
            ...(alternative === 'custom' && { customValue: parseFloat(customValue) }),
            ...(alternative === 'per_kind' && { kindRules }),
          },
        },
        { withCredentials: true }
      );
      setPostingPreview(unwrapData(res)?.posting || null);
    } catch (e) {
      setPostingPreview(null);
      toast.error(k1ReasonMessage(e));
    }
  }, [companyId, selectedYear, alternative, customValue, kindRules]);

  useEffect(() => {
    if (!preview || !alternative) return;
    if (alternative === 'custom' && !(parseFloat(customValue) >= 0)) {
      setPostingPreview(null);
      return;
    }
    fetchPostingPreview();
  }, [preview, alternative, customValue, kindRules, fetchPostingPreview]);

  const blocking = useMemo(
    () => (preview?.reviewRequirements || []).filter((r) => r.blocking),
    [preview]
  );
  const nonBlocking = useMemo(
    () => (preview?.reviewRequirements || []).filter((r) => !r.blocking),
    [preview]
  );
  const alreadyPosted = !!preview?.existingPoolYear;

  // Register attestation (independent review 2026-09-05, F3): kompletterings-
  // regeln is computed from the tillgångsregister, so it waits for the user's
  // statement that the register is complete — per pool and fiscal year. The
  // statement is stored on the server, bound to the register as it is now,
  // and lapses when the register changes. Plain Swedish, an explicit tick and
  // a button; never implied by a single registered asset.
  const [attestChecked, setAttestChecked] = useState({});
  const [attesting, setAttesting] = useState(null);
  const handleAttest = async (pool) => {
    if (!selectedYear?.value || !attestChecked[pool]) return;
    setAttesting(pool);
    try {
      await axios.post(
        getApiUrl(`${companyId}/assets/k1/register-attestations`),
        { fiscalYearId: selectedYear.value, pool },
        { withCredentials: true }
      );
      toast.success('Tillgångsregistret är intygat — kompletteringsregeln beräknas.');
      setAttestChecked((prev) => ({ ...prev, [pool]: false }));
      await fetchPreview(selectedYear.value);
    } catch (e) {
      toast.error(k1ReasonMessage(e));
    } finally {
      setAttesting(null);
    }
  };
  const handleWithdrawAttestation = async (pool) => {
    if (!selectedYear?.value) return;
    try {
      await axios.delete(
        getApiUrl(`${companyId}/assets/k1/register-attestations`),
        { data: { fiscalYearId: selectedYear.value, pool }, withCredentials: true }
      );
      toast.success('Intyget är återkallat.');
      await fetchPreview(selectedYear.value);
    } catch (e) {
      toast.error(k1ReasonMessage(e));
    }
  };
  const MEMBER_CLASS_LABELS = {
    depreciable: 'Avskrivningsbar',
    contra: 'Ackumulerade avskrivningar',
    excluded: 'Ingår inte i underlaget',
  };
  // UX_PLAN.md P11: a non-blocking requirement gets a headline that says what
  // is missing and a line that says it does not stop the bokslut; the backend
  // message stays as the body. Headlines by reason, not by parsing the text.
  const REQUIREMENT_HEADLINES = {
    K1_POOL_OPENING_UNREGISTERED: 'Tillgångsregistret saknar de inventarier du redan äger',
    K1_POOL_REGISTER_ATTESTATION_REQUIRED: 'Intyga att tillgångsregistret är komplett för att kunna använda kompletteringsregeln',
    K1_POOL_REGISTER_ATTESTATION_STALE: 'Registret har ändrats — intyga på nytt',
  };
  const REASSURANCE = 'Detta stoppar inte bokslutet: huvudregeln kan användas nu.';

  const handlePost = async () => {
    if (!postingPreview || !preview) return;
    setPosting(true);
    try {
      await axios.post(
        getApiUrl(`${companyId}/assets/k1/year-end/post`),
        {
          fiscalYearId: selectedYear.value,
          selection: {
            alternative,
            ...(alternative === 'custom' && { customValue: parseFloat(customValue) }),
            ...(alternative === 'per_kind' && { kindRules }),
          },
          expected: {
            underlag: preview.alternatives.underlag,
            closingValue: postingPreview.closingValue,
          },
        },
        { withCredentials: true }
      );
      toast.success('Årets värdering är bokförd.');
      await fetchPreview(selectedYear.value);
      await loadPoolYears();
    } catch (e) {
      toast.error(k1ReasonMessage(e));
    } finally {
      setPosting(false);
    }
  };

  const handleUndo = async (poolId) => {
    try {
      await axios.post(
        getApiUrl(`${companyId}/assets/k1/year-end/${poolId}/undo`),
        {},
        { withCredentials: true }
      );
      toast.success('Årsvärderingen är ångrad.');
      if (selectedYear?.value) await fetchPreview(selectedYear.value);
      await loadPoolYears();
    } catch (e) {
      toast.error(k1ReasonMessage(e));
    }
  };

  const alternativeOptions = useMemo(() => {
    if (!preview) return [];
    const alt = preview.alternatives;
    return [
      {
        key: 'main_rule',
        available: true,
        value: alt.mainRuleValue,
        hint: 'IL 18 kap. 13 § — högst 30 % av underlaget per år.',
      },
      {
        key: 'suppl_rule',
        available: alt.supplRuleValue !== null,
        value: alt.supplRuleValue,
        hint: alt.supplRuleValue !== null
          ? 'IL 18 kap. 17 § — värdet skrivs ned till anskaffningsvärde minus 20 % per innehavsår (jämkat vid brutet räkenskapsår).'
          : 'Inte tillgänglig (registret eller räkenskapsårshistoriken är ofullständig).',
      },
      ...(Object.values(alt.perKind || {}).filter((k) => k.underlag > 0 || k.gross > 0).length > 1 ? [{
        key: 'per_kind',
        available: true,
        value: null,
        hint: 'Kompletteringsregeln får tillämpas särskilt för varje slag av inventarier (IL 18 kap. 17 § andra meningen) — välj regel per slag nedan.',
      }] : []),
      {
        key: 'low_value_full',
        available: alt.lowValueAvailable,
        value: alt.lowValueAvailable ? 0 : null,
        hint: `Tillgänglig när underlaget är högst ${formatCurrency(alt.lowValueLimit)} (halvt prisbasbelopp, IL 18 kap. 13 §).`,
      },
      {
        key: 'custom',
        available: true,
        value: null,
        hint: `Valfritt värde mellan ${formatCurrency(alt.minimumClosingValue)} och ${formatCurrency(alt.underlag)}.`,
      },
    ];
  }, [preview]);

  return (
    <div className="asset-form" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <style>{ASSET_FORM_STYLES}</style>

      <div className="modern-card mb-0">
        <div style={{ padding: '1.25rem 1.5rem' }}>
          <div className="d-flex align-items-center gap-2 mb-2">
            <Scale size={18} style={{ color: 'var(--primary)' }} />
            <span style={{ fontWeight: 700, color: 'var(--title)' }}>Årets värdering (K1)</span>
          </div>
          <p style={{ fontSize: '0.9375rem', color: 'var(--title)', marginBottom: '0.25rem' }}>
            Här räknar LedgerLoop ut årets avskrivning på maskiner och inventarier och bokför den
            i ett verifikat. Du väljer vilken regel som ska gälla.
          </p>
          <p style={{ fontSize: '0.8125rem', color: 'var(--secondary)', marginBottom: '0.75rem' }}>
            Maskiner och inventarier värderas kollektivt till det skattemässiga värdet
            (förenklat årsbokslut, BFNAR 2006:1). Byggnader och markanläggningar skrivs av
            med samma belopp som dras av vid beskattningen. Allt bokförs i ett verifikat
            på räkenskapsårets sista dag.
          </p>
          <div style={{ maxWidth: 380 }}>
            <Select
              options={fiscalYears.map((fy) => ({
                value: fy.fiscal_year_id,
                label: `${fy.year} (${String(fy.start_date).slice(0, 10)} – ${String(fy.end_date).slice(0, 10)})${fy.state === 'closed' ? ' — stängt' : ''}`,
              }))}
              value={selectedYear}
              onChange={setSelectedYear}
              placeholder="Välj räkenskapsår"
              theme={brandSelectTheme}
              aria-label="Välj räkenskapsår"
            />
          </div>
        </div>
      </div>

      {error && <Alert variant="warning" className="mb-0">{error}</Alert>}
      {loading && (
        <div className="text-center py-4"><Spinner animation="border" variant="primary" size="sm" /></div>
      )}

      {preview && !loading && (
        <>
          {alreadyPosted && (
            <Alert variant="success" className="mb-0 d-flex align-items-center gap-2">
              <CheckCircle2 size={18} />
              <span>
                Årets värdering är bokförd — valt värde{' '}
                {formatCurrency(preview.existingPoolYear.selected_closing_value, 2)}
                {' '}({K1_ALTERNATIVE_LABELS[preview.existingPoolYear.selected_alternative] || preview.existingPoolYear.selected_alternative}).
              </span>
            </Alert>
          )}

          {blocking.map((r, i) => (
            <Alert key={i} variant="danger" className="mb-0 d-flex align-items-start gap-2">
              <AlertTriangle size={18} style={{ flexShrink: 0, marginTop: 2 }} />
              <span>{r.message}</span>
            </Alert>
          ))}
          {nonBlocking.map((r, i) => (
            <Alert key={i} variant="warning" className="mb-0">
              {REQUIREMENT_HEADLINES[r.reason] && (
                <div style={{ fontWeight: 700, marginBottom: '0.25rem' }}>{REQUIREMENT_HEADLINES[r.reason]}</div>
              )}
              <div style={{ fontSize: '0.8125rem' }}>{r.message}</div>
              {REQUIREMENT_HEADLINES[r.reason] && (
                <div style={{ fontSize: '0.8125rem', marginTop: '0.25rem', color: 'var(--primary)' }}>{REASSURANCE}</div>
              )}
              {/* Gate 1 F3: an imported opening balance without register rows
                  withholds kompletteringsregeln; the way out is to register the
                  assets the company already owns (no posting), so link there. */}
              {r.action === 'register_existing_assets' && (
                <div className="mt-2">
                  <a href={`/${companyId}/assets?skapa=1`} className="fw-semibold">
                    Registrera befintliga tillgångar i tillgångsregistret
                  </a>
                </div>
              )}
              {r.action === 'attest_register' && (
                <div className="mt-2">
                  <Form.Check
                    type="checkbox"
                    id={`k1-attest-${r.pool}`}
                    checked={Boolean(attestChecked[r.pool])}
                    onChange={(e) => {
                      // Read the value NOW: React restores a controlled
                      // checkbox before a lazy state updater would run.
                      const next = e.target.checked;
                      setAttestChecked((prev) => ({ ...prev, [r.pool]: next }));
                    }}
                    label={(
                      <span>
                        Jag intygar att tillgångsregistret innehåller alla maskiner och inventarier som
                        företaget ägde vid räkenskapsårets början, med anskaffningsvärde och anskaffningsdatum
                        (förteckning enligt BFNAR 2006:1 punkt 6.1). Kompletteringsregeln (IL 18 kap. 17 §)
                        beräknas på registrets uppgifter — saknas tillgångar blir avskrivningen för hög.
                      </span>
                    )}
                  />
                  <Button
                    size="sm"
                    variant="outline-primary"
                    className="mt-2 d-inline-flex align-items-center gap-1"
                    disabled={!attestChecked[r.pool] || attesting === r.pool}
                    onClick={() => handleAttest(r.pool)}
                  >
                    <ShieldCheck size={14} /> {attesting === r.pool ? 'Intygar…' : 'Intyga att registret är komplett'}
                  </Button>
                </div>
              )}
            </Alert>
          ))}
          {Object.entries(preview.registerAttestations || {}).map(([pool, attestation]) => (
            <Alert key={`attested-${pool}`} variant="light" className="mb-0 py-2 d-flex align-items-center gap-2" style={{ fontSize: '0.8125rem' }}>
              <ShieldCheck size={16} style={{ color: 'var(--primary)' }} />
              <span>
                Tillgångsregistret för {K1_STATUTORY_KIND_LABELS[pool] || pool} är intygat komplett{' '}
                {attestation.attested_at ? formatDate(attestation.attested_at) : ''} ({attestation.register_asset_count} tillgångar,{' '}
                {formatCurrency(attestation.register_acquisition_value, 2)}) — kompletteringsregeln beräknas på registret.{' '}
                <Button variant="link" size="sm" className="p-0 align-baseline" onClick={() => handleWithdrawAttestation(pool)}>
                  Återkalla intyget
                </Button>
              </span>
            </Alert>
          ))}

          <div className="modern-card mb-0">
            <div style={{ padding: '1.25rem 1.5rem' }}>
              <div style={{ fontWeight: 700, color: 'var(--title)', marginBottom: '0.75rem' }}>
                Underlag för maskiner och inventarier
              </div>
              <table className="table table-sm asset-table mb-0" style={{ maxWidth: 560 }}>
                <tbody>
                  <tr><td>Bokfört värde vid årets början</td><td className="asset-num text-end">{formatCurrency(preview.openingBookValue, 2)}</td></tr>
                  <tr><td>Årets anskaffningar (kvar vid årets slut)</td><td className="asset-num text-end">{formatCurrency(preview.acquisitionsKept, 2)}</td></tr>
                  <tr><td>Försäljningsinkomster och försäkringsersättningar (äldre innehav)</td><td className="asset-num text-end">−{formatCurrency(preview.proceedsPreYear, 2)}</td></tr>
                  <tr style={{ fontWeight: 700 }}><td>Avskrivningsunderlag (IL 18 kap. 13 §)</td><td className="asset-num text-end">{formatCurrency(preview.alternatives.underlag, 2)}</td></tr>
                  <tr><td className="text-muted">Bokfört värde före årets avskrivning</td><td className="asset-num text-end text-muted">{formatCurrency(preview.bookedPreDepreciation, 2)}</td></tr>
                </tbody>
              </table>
            </div>
          </div>

          {(preview.pools || []).some((p) => (p.members || []).length + (p.excludedMembers || []).length > 0) && (
            <div className="modern-card mb-0">
              <div style={{ padding: '1.25rem 1.5rem' }}>
                <div style={{ fontWeight: 700, color: 'var(--title)', marginBottom: '0.25rem' }}>
                  Konton i värderingen
                </div>
                <p style={{ fontSize: '0.8125rem', color: 'var(--secondary)' }}>
                  Varje konto i intervallen 1000–1099 och 1200–1299 klassas efter kontoplanen: avskrivningsbara
                  tillgångskonton bildar den kollektiva värderingen, ackumulerade avskrivningar är dess negativa
                  delar, och förskott, pågående nyanläggningar och konst ligger utanför underlaget
                  (BFNAR 2006:1 punkt 6.24, 6.29, 6.59; IL 18 kap. 3 §). Egna konton i intervallen räknas som
                  avskrivningsbara och visas här. Stämmer klassningen av ett eget konto inte, går den ännu inte att
                  ändra här — hör av dig till supporten.
                </p>
                {(preview.pools || []).map((pool) => {
                  const rows = [...(pool.members || []), ...(pool.excludedMembers || [])]
                    .filter((m) => Math.abs(m.opening || 0) > 0.005 || Math.abs(m.end || 0) > 0.005
                      || (m.debits || 0) > 0.005 || (m.credits || 0) > 0.005 || m.own);
                  if (rows.length === 0) return null;
                  return (
                    <div key={pool.code} className="mb-3">
                      <div style={{ fontWeight: 600, fontSize: '0.8125rem', color: 'var(--title)' }}>
                        {K1_STATUTORY_KIND_LABELS[pool.code] || pool.label}
                      </div>
                      <div style={{ overflowX: 'auto' }}>
                        <table className="table table-sm asset-table mb-0">
                          <thead>
                            <tr>
                              <th>Konto</th><th>Namn</th><th>Klass</th>
                              <th className="text-end">Ingående</th>
                              <th className="text-end">Debet</th>
                              <th className="text-end">Kredit</th>
                              <th className="text-end">Utgående</th>
                            </tr>
                          </thead>
                          <tbody>
                            {rows.map((m) => (
                              <tr key={m.account}>
                                <td className="asset-num">{m.account}</td>
                                <td style={{ fontSize: '0.75rem' }}>
                                  {m.name}
                                  {m.own && <OwnAccountChip />}
                                  {m.archived && <span className="badge bg-dark ms-1" style={{ fontSize: '0.65rem' }}>Arkiverat</span>}
                                </td>
                                <td style={{ fontSize: '0.75rem' }} title={m.classificationReason || undefined}>
                                  {MEMBER_CLASS_LABELS[m.classification] || (m.contra ? MEMBER_CLASS_LABELS.contra : MEMBER_CLASS_LABELS.depreciable)}
                                  {m.classificationReason && <div style={{ color: 'var(--secondary)' }}>{m.classificationReason}</div>}
                                </td>
                                <td className="asset-num text-end">{formatCurrency(m.opening || 0, 2)}</td>
                                <td className="asset-num text-end">{formatCurrency(m.debits || 0, 2)}</td>
                                <td className="asset-num text-end">{formatCurrency(m.credits || 0, 2)}</td>
                                <td className="asset-num text-end">{formatCurrency(m.end || 0, 2)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {!alreadyPosted && blocking.length === 0 && (
            <div className="modern-card mb-0">
              <div style={{ padding: '1.25rem 1.5rem' }}>
                <div style={{ fontWeight: 700, color: 'var(--title)', marginBottom: '0.75rem' }}>
                  Välj värderingsalternativ
                </div>
                {alternativeOptions.map((opt) => (
                  <Form.Check
                    key={opt.key}
                    type="radio"
                    id={`k1-alt-${opt.key}`}
                    name="k1-alternative"
                    className="mb-2"
                    disabled={!opt.available}
                    checked={alternative === opt.key}
                    onChange={() => setAlternative(opt.key)}
                    label={(
                      <span>
                        <strong>{K1_ALTERNATIVE_LABELS[opt.key]}</strong>
                        {opt.value !== null && opt.available && (
                          <span className="asset-num"> — utgående värde {formatCurrency(opt.value, 2)}</span>
                        )}
                        <br />
                        <span style={{ fontSize: '0.75rem', color: 'var(--secondary)' }}>{opt.hint}</span>
                      </span>
                    )}
                  />
                ))}
                {alternative === 'per_kind' && (
                  <div className="mt-2" style={{ paddingLeft: '1.5rem' }}>
                    {Object.entries(preview.alternatives.perKind || {})
                      .filter(([, k]) => k.underlag > 0 || k.gross > 0)
                      .map(([kind, k]) => (
                        <div key={kind} className="mb-2">
                          <div style={{ fontWeight: 600, fontSize: '0.8125rem', color: 'var(--title)' }}>
                            {K1_STATUTORY_KIND_LABELS[kind] || kind}
                          </div>
                          <Form.Check
                            inline
                            type="radio"
                            id={`k1-kind-${kind}-main`}
                            name={`k1-kind-${kind}`}
                            label={`Huvudregeln — ${formatCurrency(k.mainRuleValue, 2)}`}
                            checked={(kindRules[kind] || 'main') === 'main'}
                            onChange={() => setKindRules((prev) => ({ ...prev, [kind]: 'main' }))}
                          />
                          <Form.Check
                            inline
                            type="radio"
                            id={`k1-kind-${kind}-suppl`}
                            name={`k1-kind-${kind}`}
                            disabled={k.supplRuleValue === null}
                            label={k.supplRuleValue !== null
                              ? `Kompletteringsregeln — ${formatCurrency(k.supplRuleValue, 2)}`
                              : 'Kompletteringsregeln — ej tillgänglig'}
                            checked={kindRules[kind] === 'suppl'}
                            onChange={() => setKindRules((prev) => ({ ...prev, [kind]: 'suppl' }))}
                          />
                        </div>
                      ))}
                  </div>
                )}
                {/* UX_PLAN.md P11: the question a first-time user asks is "how
                    much is this year's depreciation?" — answered here from the
                    posting preview for the chosen alternative. */}
                {postingPreview && alternative !== 'custom' && (
                  <div className="mt-2" style={{ fontSize: '0.875rem', color: 'var(--title)' }}>
                    Årets avskrivning med det valda alternativet:{' '}
                    <strong className="asset-num">{formatCurrency(postingPreview.annualDepreciation, 2)}</strong>
                  </div>
                )}
                {alternative === 'custom' && (
                  <div style={{ maxWidth: 260 }} className="mt-2">
                    <Form.Label>Utgående värde (kr)</Form.Label>
                    <Form.Control
                      type="number"
                      min="0"
                      step="0.01"
                      value={customValue}
                      onChange={(e) => setCustomValue(e.target.value)}
                      aria-label="Eget utgående värde"
                    />
                  </div>
                )}
              </div>
            </div>
          )}

          {preview.propertyLines?.length > 0 && (
            <div className="modern-card mb-0">
              <div style={{ padding: '1.25rem 1.5rem' }}>
                <div style={{ fontWeight: 700, color: 'var(--title)', marginBottom: '0.75rem' }}>
                  Byggnader och markanläggningar (skattemässig avskrivning)
                </div>
                <div style={{ overflowX: 'auto' }}>
                  <table className="table table-sm asset-table mb-0">
                    <thead>
                      <tr>
                        <th>Tillgång</th><th>Klassificering</th>
                        <th className="text-end">Procent</th>
                        <th className="text-end">Dagar</th>
                        <th className="text-end">Årets avskrivning</th>
                      </tr>
                    </thead>
                    <tbody>
                      {preview.propertyLines.map((line) => (
                        <tr key={line.asset_id}>
                          <td>{line.name}</td>
                          <td style={{ fontSize: '0.75rem' }}>{line.classification}</td>
                          <td className="asset-num text-end">{formatNumber(line.rate * 100, 1)} %</td>
                          <td className="asset-num text-end">{line.heldDays}/{line.fyDays}</td>
                          <td className="asset-num text-end">
                            {line.alreadyBookedThisYear ? 'Redan bokförd' : formatCurrency(line.amount, 2)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {postingPreview && !alreadyPosted && blocking.length === 0 && (
            <div className="modern-card mb-0">
              <div style={{ padding: '1.25rem 1.5rem' }}>
                <div style={{ fontWeight: 700, color: 'var(--title)', marginBottom: '0.25rem' }}>
                  Bokföringsförslag
                </div>
                <p style={{ fontSize: '0.8125rem', color: 'var(--secondary)' }}>
                  {postingPreview.annualDepreciation >= 0
                    ? <>Årets avskrivning maskiner/inventarier: <strong>{formatCurrency(postingPreview.annualDepreciation, 2)}</strong></>
                    : <>Intäkt vid kollektiv värdering: <strong>{formatCurrency(-postingPreview.annualDepreciation, 2)}</strong> (BFNAR 2006:1 p. 7.4)</>}
                  {' '}— bokförs som ett par per pool: avskrivningskonto mot ackumulerade avskrivningar (kontorollerna för K1-poolerna).
                </p>
                {postingPreview.notices?.map((n, i) => (
                  <Alert key={i} variant="info" className="py-2" style={{ fontSize: '0.8125rem' }}>{n.message}</Alert>
                ))}
                <div style={{ overflowX: 'auto' }}>
                  <table className="table table-sm asset-table mb-3">
                    <thead>
                      <tr><th>Konto</th><th>Beskrivning</th><th className="text-end">Debet</th><th className="text-end">Kredit</th></tr>
                    </thead>
                    <tbody>
                      {postingPreview.rows.map((row, i) => (
                        <tr key={i}>
                          <td className="asset-num">{row.account}</td>
                          <td style={{ fontSize: '0.75rem' }}>{row.label}</td>
                          <td className="asset-num text-end">{row.type === 'debit' ? formatCurrency(row.amount, 2) : ''}</td>
                          <td className="asset-num text-end">{row.type === 'credit' ? formatCurrency(row.amount, 2) : ''}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <Button variant="primary" disabled={posting} onClick={handlePost}>
                  {posting ? 'Bokför…' : 'Bokför årets värdering'}
                </Button>
              </div>
            </div>
          )}
        </>
      )}

      {poolYears.length > 0 && (
        <div className="modern-card mb-0">
          <div style={{ padding: '1.25rem 1.5rem' }}>
            <div style={{ fontWeight: 700, color: 'var(--title)', marginBottom: '0.75rem' }}>
              Bokförda årsvärderingar
            </div>
            <div style={{ overflowX: 'auto' }}>
              <table className="table table-sm asset-table mb-0">
                <thead>
                  <tr>
                    <th>År</th><th>Alternativ</th>
                    <th className="text-end">Underlag</th>
                    <th className="text-end">Utgående värde</th>
                    <th className="text-end">Årets avskrivning</th>
                    <th>Bokförd</th><th />
                  </tr>
                </thead>
                <tbody>
                  {poolYears.map((py, index) => (
                    <tr key={py.pool_id}>
                      <td>{py.year}</td>
                      <td style={{ fontSize: '0.75rem' }}>{K1_ALTERNATIVE_LABELS[py.selected_alternative] || py.selected_alternative}</td>
                      <td className="asset-num text-end">{formatCurrency(py.underlag, 2)}</td>
                      <td className="asset-num text-end">{formatCurrency(py.selected_closing_value, 2)}</td>
                      <td className="asset-num text-end">{formatCurrency(py.annual_depreciation, 2)}</td>
                      <td style={{ fontSize: '0.75rem' }}>{formatDate(py.posted_at)}</td>
                      <td className="text-end">
                        {index === 0 && (
                          <Button
                            variant="outline-secondary"
                            size="sm"
                            className="d-inline-flex align-items-center gap-1"
                            onClick={() => handleUndo(py.pool_id)}
                          >
                            <Undo2 size={13} /> Ångra
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default K1YearEndPanel;
